import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { MessageSquare, X, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";
import type { Session } from "@supabase/supabase-js";

type Message = {
  id: string;
  message: string;
  is_bot: boolean;
  created_at: string;
};

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const sessionIdRef = useRef(uuidv4());
  const initialMessageShownRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Set up auth listener and get initial session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Show initial message when chat is opened
  useEffect(() => {
    if (isOpen && !initialMessageShownRef.current && messages.length === 0) {
      initialMessageShownRef.current = true;
      const welcomeMessage = {
        id: uuidv4(),
        message: "Hello! Welcome to TheGrowthBy! How can we help you today?",
        is_bot: true,
        created_at: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
      
      // Store welcome message (only if user is authenticated)
      if (session?.user) {
        supabase
          .from("chat_messages")
          .insert({
            session_id: sessionIdRef.current,
            message: welcomeMessage.message,
            is_bot: true,
            user_id: session.user.id,
          })
          .then(({ error }) => {
            if (error) console.error("Error storing welcome message:", error);
          });
      }
    }
  }, [isOpen, messages.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    // Require authentication for chat
    if (!session?.user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to use the chat feature.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const userMessage = message;
    setMessage("");

    try {
      // Store user message
      const { error: insertError } = await supabase
        .from("chat_messages")
        .insert({
          session_id: sessionIdRef.current,
          message: userMessage,
          is_bot: false,
          user_id: session.user.id,
        });

      if (insertError) throw insertError;

      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          message: userMessage,
          is_bot: false,
          created_at: new Date().toISOString(),
        },
      ]);

      // Get AI response from Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('chat', {
        body: { message: userMessage }
      });

      if (error) throw error;

      const botMessage = data.message;

      // Store bot message
      const { error: botInsertError } = await supabase
        .from("chat_messages")
        .insert({
          session_id: sessionIdRef.current,
          message: botMessage,
          is_bot: true,
          user_id: session.user.id,
        });

      if (botInsertError) throw botInsertError;

      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          message: botMessage,
          is_bot: true,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {isOpen ? (
        <div className="bg-white rounded-lg shadow-xl w-80 sm:w-96 flex flex-col h-[500px]">
          <div className="p-4 border-b flex justify-between items-center bg-blue-600 text-white rounded-t-lg">
            <h3 className="font-semibold">Chat with us</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-blue-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.is_bot ? "justify-start" : "justify-end"
                }`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.is_bot
                      ? "bg-gray-100 text-gray-800"
                      : "bg-blue-600 text-white"
                  }`}
                >
                  {msg.message}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="p-4 border-t">
            <div className="flex gap-2">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="resize-none"
                rows={1}
                disabled={isLoading}
              />
              <Button
                type="submit"
                disabled={isLoading}
                className="shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <Button
          onClick={() => setIsOpen(true)}
          size="lg"
          className="rounded-full h-14 w-14 shadow-lg"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
};
